import React from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import classNames from 'classnames';

import {numberFormattedText} from '@ey/utils';
import {numberFormatsListItems} from '../../../util/uiconstants';

import OutputText from '@ey/canvascoreservices/OutputText';

import StyledDefaultVariant from './StyledDefaultVariant';
import StyledBadgeVariant from './StyledBadgeVariant';
import StyledBigVariant from './StyledBigVariant';
import StyledColoredVariant from './StyledColoredVariant';
import StyledPillVariant from './StyledPillVariant';
import StyledPillOutlineVariant from './StyledPillOutlineVariant';
import StyledStripeVariant from './StyledStripeVariant';
import Badge from '../../CoreWrappers/Badge';

export const CD_DEFAULT = 'default';
export const CD_BADGE = 'badge';
export const CD_BIG = 'big';
export const CD_COLORED = 'colored';
export const CD_PILL = 'pill';
export const CD_PILL_OUTLINE = 'pillOutline';
export const CD_STRIPE = 'stripe';

const CountDisplay = ({
	variant = CD_DEFAULT,
	count,
	label,
	singleLabel = null,
	className = null,
	color = null,
	...rest
}) => {
	const COUNT_TYPE_TEXT = 'text';
	const COUNT_TYPE_BADGE = 'badge';
	const COUNT_TYPE_BIG = 'big';

	const variants = {
		[CD_DEFAULT]: {Wrapper: StyledDefaultVariant, countType: COUNT_TYPE_TEXT, isLabelLight: true},
		[CD_BADGE]: {Wrapper: StyledBadgeVariant, countType: COUNT_TYPE_BADGE, isLabelLight: true},
		[CD_BIG]: {Wrapper: StyledBigVariant, countType: COUNT_TYPE_BIG, isLabelLight: false},
		[CD_COLORED]: {Wrapper: StyledDefaultVariant, countType: COUNT_TYPE_TEXT, isLabelLight: true},
		[CD_PILL]: {Wrapper: StyledDefaultVariant, countType: COUNT_TYPE_TEXT, isLabelLight: true},
		[CD_PILL_OUTLINE]: {Wrapper: StyledDefaultVariant, countType: COUNT_TYPE_TEXT, isLabelLight: true},
		[CD_STRIPE]: {Wrapper: StyledDefaultVariant, countType: COUNT_TYPE_TEXT, isLabelLight: true}
	};

	const {Wrapper, countType, isLabelLight} = variants[variant];

	const numberFormat = useSelector(({userPreferences}) => userPreferences.numberFormat);
	const convertedNumberFormat = numberFormatsListItems.find(({label}) => label === numberFormat)?.id;
	const formattedCount = numberFormattedText(convertedNumberFormat !== undefined ? convertedNumberFormat : 0, count);

	let displayLabel = label;
	if (singleLabel) displayLabel = count === 1 ? singleLabel : label;
	let displayTitle = `${formattedCount} ${displayLabel}`;
	if (variant === CD_BIG) displayTitle = `${displayLabel} ${formattedCount}`;

	const colorProps = {};
	if (color) colorProps.className = `cd-${color}`;

	const props = {
		title: displayTitle,
		className: classNames('count-display', {[className]: className !== null}),
		...rest
	};

	return (
		<Wrapper {...props}>
			{countType === COUNT_TYPE_TEXT && <OutputText body2>{formattedCount}</OutputText>}
			{countType === COUNT_TYPE_BIG && <OutputText h2 {...colorProps}>{formattedCount}</OutputText>}
			{countType === COUNT_TYPE_BADGE && <Badge value={formattedCount} />}
			<OutputText body2 light={isLabelLight} ellipsis className="cd-label">
				{displayLabel}
			</OutputText>
		</Wrapper>
	);
};

CountDisplay.propTypes = {
	variant: PropTypes.oneOf([CD_DEFAULT, CD_COLORED, CD_PILL, CD_PILL_OUTLINE, CD_STRIPE, CD_BIG]),
	count: PropTypes.number.isRequired,
	label: PropTypes.string.isRequired,
	singleLabel: PropTypes.string,
	className: PropTypes.string,
	color: PropTypes.oneOf(['purple', 'blue', 'teal', 'maroon', 'orange'])
};

export default CountDisplay;
